export const DELETE_ACCELERATION_START_MULTIPLIER = 1.5
export const DELETE_ACCELERATION_MAX_MULTIPLIER = 6.5
export const DELETE_ACCELERATION_RAMP_MS = 1_200
export const CONTROLLED_DELETE_INITIAL_DELAY_MS = 180
export const CONTROLLED_DELETE_START_INTERVAL_MS = 95
export const CONTROLLED_DELETE_MIN_INTERVAL_MS = 18

type DeleteDirection = 'backward' | 'forward'

export type DeleteAccelerationState = {
  disabled: true
}

export type DeleteAccelerationInput = {
  previousValue: string
  nextValue: string
  selectionStart: number
  selectionEnd: number
  inputType: string | null
  isComposing: boolean
  nowMs: number
  state: DeleteAccelerationState
}

export type DeleteAccelerationResult = {
  value: string
  caret: number
  accelerated: boolean
}

export type ControlledDeleteInput = {
  value: string
  selectionStart: number
  selectionEnd: number
  direction?: DeleteDirection
}

export type ControlledDeleteResult = {
  value: string
  caret: number
  deleted: boolean
}

export function createDeleteAccelerationState(): DeleteAccelerationState {
  return { disabled: true }
}

export function resetDeleteAcceleration(_state: DeleteAccelerationState): void {
  // Native Android keyboard events do not expose a reliable finger-up signal.
  // Keep native delete safe: one visible key action deletes one character.
}

function deleteDirectionForInputType(inputType: string | null): DeleteDirection | null {
  if (inputType === 'deleteContentForward') return 'forward'
  if (inputType === null || inputType === 'deleteContentBackward') return 'backward'
  return null
}

function findSingleDeletion(previousValue: string, nextValue: string): { start: number } | null {
  if (previousValue.length - nextValue.length !== 1) return null

  let prefixLength = 0
  while (
    prefixLength < previousValue.length &&
    prefixLength < nextValue.length &&
    previousValue[prefixLength] === nextValue[prefixLength]
  ) {
    prefixLength += 1
  }

  let suffixLength = 0
  while (
    suffixLength < previousValue.length - prefixLength &&
    suffixLength < nextValue.length - prefixLength &&
    previousValue[previousValue.length - 1 - suffixLength] === nextValue[nextValue.length - 1 - suffixLength]
  ) {
    suffixLength += 1
  }

  const removedLength = previousValue.length - prefixLength - suffixLength
  const insertedLength = nextValue.length - prefixLength - suffixLength
  if (removedLength !== 1 || insertedLength !== 0) return null
  return { start: prefixLength }
}

function previousCodePointStart(value: string, index: number): number {
  if (index <= 0) return 0
  const last = value.charCodeAt(index - 1)
  const previous = index > 1 ? value.charCodeAt(index - 2) : 0
  if (last >= 0xdc00 && last <= 0xdfff && previous >= 0xd800 && previous <= 0xdbff) {
    return index - 2
  }
  return index - 1
}

function nextCodePointEnd(value: string, index: number): number {
  if (index >= value.length) return value.length
  const current = value.charCodeAt(index)
  const next = index + 1 < value.length ? value.charCodeAt(index + 1) : 0
  if (current >= 0xd800 && current <= 0xdbff && next >= 0xdc00 && next <= 0xdfff) {
    return index + 2
  }
  return index + 1
}

export function controlledDeleteIntervalMs(heldMs: number): number {
  const rampProgress = Math.min(1, Math.max(0, heldMs) / DELETE_ACCELERATION_RAMP_MS)
  return Math.round(
    CONTROLLED_DELETE_START_INTERVAL_MS -
      (CONTROLLED_DELETE_START_INTERVAL_MS - CONTROLLED_DELETE_MIN_INTERVAL_MS) * rampProgress,
  )
}

export function deleteComposerControlled(input: ControlledDeleteInput): ControlledDeleteResult {
  const { value, selectionStart, selectionEnd, direction = 'backward' } = input
  const start = Math.max(0, Math.min(selectionStart, selectionEnd))
  const end = Math.min(value.length, Math.max(selectionStart, selectionEnd))

  if (start !== end) {
    return {
      value: value.slice(0, start) + value.slice(end),
      caret: start,
      deleted: true,
    }
  }

  if (direction === 'backward') {
    if (start <= 0) return { value, caret: start, deleted: false }
    const deleteStart = previousCodePointStart(value, start)
    return {
      value: value.slice(0, deleteStart) + value.slice(start),
      caret: deleteStart,
      deleted: true,
    }
  }

  if (start >= value.length) return { value, caret: start, deleted: false }
  const deleteEnd = nextCodePointEnd(value, start)
  return {
    value: value.slice(0, start) + value.slice(deleteEnd),
    caret: start,
    deleted: true,
  }
}

export function accelerateComposerDelete(input: DeleteAccelerationInput): DeleteAccelerationResult {
  const {
    previousValue,
    nextValue,
    selectionStart,
    selectionEnd,
    inputType,
    isComposing,
    state,
  } = input
  const unchanged = { value: nextValue, caret: selectionStart, accelerated: false }
  const direction = deleteDirectionForInputType(inputType)
  if (isComposing || direction === null || selectionStart !== selectionEnd || nextValue.length === 0) {
    resetDeleteAcceleration(state)
    return unchanged
  }

  const deletion = findSingleDeletion(previousValue, nextValue)
  if (!deletion || selectionStart !== deletion.start) {
    resetDeleteAcceleration(state)
    return unchanged
  }

  resetDeleteAcceleration(state)
  return unchanged
}
